import { useState, useCallback, useEffect } from "react";
import {
  signIn,
  completeNewPassword,
  getCurrentSessionToken,
  signOutCurrentUser,
  getCurrentUserEmail,
} from "../services/cognito";
import type { CognitoUser } from "amazon-cognito-identity-js";

export type AuthStep = "LOGIN" | "NEW_PASSWORD" | "AUTHENTICATED";

interface UseLoginReturn {
  username: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  error: string;
  isLoading: boolean;
  step: AuthStep;
  userEmail: string | null;
  setUsername: (value: string) => void;
  setPassword: (value: string) => void;
  setNewPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  handleLogin: () => Promise<void>;
  handleNewPassword: () => Promise<void>;
  handleLogout: () => void;
  handleSocialLogin: (provider: "Google" | "HostedUI") => void;
  isInitializing: boolean;
}

// Helper functions for Cognito OAuth handling
function parseHashParams(): Record<string, string> {
  const hash = window.location.hash.substring(1);
  const params: Record<string, string> = {};
  if (!hash) return params;
  
  hash.split("&").forEach((part) => {
    const temp = part.split("=");
    if (temp[0]) {
      params[temp[0]] = decodeURIComponent(temp[1] || "");
    }
  });
  return params;
}

function getUsernameFromIdToken(idToken: string): string {
  try {
    const base64Url = idToken.split(".")[1];
    if (!base64Url) throw new Error("Malformed JWT");
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const decoded = JSON.parse(jsonPayload);
    return decoded["cognito:username"] || decoded["sub"];
  } catch (e) {
    console.error("Error decoding ID token:", e);
    throw new Error("ID token inválido");
  }
}

async function exchangeCodeForTokens(code: string): Promise<{ id_token: string; access_token: string; refresh_token?: string }> {
  const domain = process.env.BUN_PUBLIC_COGNITO_DOMAIN;
  const clientId = process.env.BUN_PUBLIC_COGNITO_WEB_CLIENT_ID;
  const redirectUri = process.env.BUN_PUBLIC_COGNITO_REDIRECT_URI;

  if (!domain || !clientId || !redirectUri) {
    throw new Error("Falta configuración de entorno de Cognito (Domain, Client ID, Redirect URI)");
  }

  const response = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code,
      redirect_uri: redirectUri,
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Error en el intercambio de código: ${errText}`);
  }

  return response.json();
}

function saveTokensToLocalStorage(clientId: string, username: string, tokens: { id_token: string; access_token: string; refresh_token?: string }) {
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`, tokens.id_token);
  localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`, tokens.access_token);
  if (tokens.refresh_token) {
    localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`, tokens.refresh_token);
  }
}

export function useLogin(): UseLoginReturn {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [step, setStep] = useState<AuthStep>("LOGIN");
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Store cognito user & attributes for the new-password challenge
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);
  const [pendingAttributes, setPendingAttributes] = useState<Record<string, string>>({});

  // Auto-login active session & handle OAuth callback
  useEffect(() => {
    const handleOAuthAndCheckSession = async () => {
      setIsInitializing(true);
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const hashParams = parseHashParams();
        const clientId = process.env.BUN_PUBLIC_COGNITO_WEB_CLIENT_ID || "";

        let tokenExchangeSuccess = false;
        let idToken = hashParams.id_token;
        let accessToken = hashParams.access_token;
        let refreshToken = hashParams.refresh_token;

        if (code) {
          try {
            const tokens = await exchangeCodeForTokens(code);
            idToken = tokens.id_token;
            accessToken = tokens.access_token;
            refreshToken = tokens.refresh_token;
            tokenExchangeSuccess = true;
          } catch (err: any) {
            console.error("Error exchanging OAuth code:", err);
            setError(err?.message || "Error al procesar el inicio de sesión con Google.");
          }
        } else if (idToken && accessToken) {
          tokenExchangeSuccess = true;
        }

        if (tokenExchangeSuccess && idToken && accessToken) {
          const extractedUsername = getUsernameFromIdToken(idToken);
          saveTokensToLocalStorage(clientId, extractedUsername, {
            id_token: idToken,
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          // Clean URL params/hash without reloading the page
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }

        // Verify session via standard Cognito helper
        const token = await getCurrentSessionToken();
        if (token) {
          const email = await getCurrentUserEmail();
          setUserEmail(email);
          setStep("AUTHENTICATED");
        }
      } catch (e) {
        console.error("Session check or OAuth processing failed:", e);
      } finally {
        setIsInitializing(false);
      }
    };
    
    handleOAuthAndCheckSession();
  }, []);

  const handleLogin = useCallback(async () => {
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Ingresá tu usuario y contraseña");
      return;
    }

    setIsLoading(true);
    try {
      const result = await signIn(username.trim(), password);

      if (result.type === "NEW_PASSWORD_REQUIRED") {
        setPendingUser(result.cognitoUser!);
        setPendingAttributes(result.userAttributes || {});
        setStep("NEW_PASSWORD");
      } else {
        const token = await getCurrentSessionToken();
        if (!token) {
          throw new Error("No se pudo obtener el token de sesión.");
        }
        const email = await getCurrentUserEmail();
        setUserEmail(email);
        setStep("AUTHENTICATED");
      }
    } catch (err: any) {
      const message =
        err?.code === "NotAuthorizedException"
          ? "Usuario o contraseña incorrectos"
          : err?.code === "UserNotFoundException"
          ? "El usuario no existe"
          : err?.code === "UserNotConfirmedException"
          ? "Tu cuenta aún no fue confirmada"
          : err?.message || "Error al iniciar sesión";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [username, password]);

  const handleNewPassword = useCallback(async () => {
    setError("");

    if (!newPassword.trim()) {
      setError("Ingresá tu nueva contraseña");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (newPassword.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    if (!pendingUser) {
      setError("Error inesperado. Intentá iniciar sesión nuevamente.");
      setStep("LOGIN");
      return;
    }

    setIsLoading(true);
    try {
      await completeNewPassword(pendingUser, newPassword, pendingAttributes);
      
      const token = await getCurrentSessionToken();
      if (!token) {
        throw new Error("No se pudo obtener el token de sesión.");
      }
      const email = await getCurrentUserEmail();
      setUserEmail(email);
      setStep("AUTHENTICATED");
    } catch (err: any) {
      const message =
        err?.code === "InvalidPasswordException"
          ? "La contraseña no cumple los requisitos de seguridad"
          : err?.message || "Error al cambiar la contraseña";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [newPassword, confirmPassword, pendingUser, pendingAttributes]);

  const handleLogout = useCallback(() => {
    try {
      signOutCurrentUser();
    } catch (e) {
      console.error("SignOut error", e);
    }
    setStep("LOGIN");
    setUsername("");
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setPendingUser(null);
    setPendingAttributes({});
    setUserEmail(null);
  }, []);

  const handleSocialLogin = useCallback((provider: "Google" | "HostedUI") => {
    const domain = process.env.BUN_PUBLIC_COGNITO_DOMAIN;
    const clientId = process.env.BUN_PUBLIC_COGNITO_WEB_CLIENT_ID;
    const redirectUri = process.env.BUN_PUBLIC_COGNITO_REDIRECT_URI;

    if (!domain || !clientId || !redirectUri) {
      setError("Falta configuración de entorno de Cognito (Domain, Client ID, Redirect URI)");
      return;
    }

    let url = `${domain}/oauth2/authorize?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    
    if (provider === "Google") {
      url += "&identity_provider=Google";
    }

    window.location.href = url;
  }, []);

  return {
    username,
    password,
    newPassword,
    confirmPassword,
    error,
    isLoading,
    step,
    userEmail,
    setUsername,
    setPassword,
    setNewPassword,
    setConfirmPassword,
    handleLogin,
    handleNewPassword,
    handleLogout,
    handleSocialLogin,
    isInitializing,
  };
}
